import React, { useState, useEffect, useMemo } from 'react';
import supabase from '../../supabase';
import AdminBar from './adminBar';
import { Helmet } from 'react-helmet-async';

// NOTE: Since the real 'supabase' client and 'AdminBar' component cannot be imported 
// in this isolated environment, they are defined inline/mocked structurally.

// --- Dependency Fix 1: Inline AdminBar ---


// --- Dependency Fix 2: Structural Supabase Mock (No Data) ---
// Mocking the Supabase client structure to allow the code to run without error,
// but returning empty arrays instead of sample data.


export default function Agents() {
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [error, setError] = useState(null);
    
    // Default date range: last 30 days
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Function to fetch all necessary data from Supabase
    const fetchData = async () => {
        setError(null);
        setLoading(true);
        try {
            // 1. Fetch Agent Data
            const { data: agentData, error: agentsError } = await supabase
                .from('agents')
                .select('id, name, referral');

            if (agentsError) throw agentsError;

            // 2. Fetch Business (Customer) Data
            const { data: businessData, error: businessError } = await supabase
                .from('businesses')
                .select('referral, referral_activated_at');

            if (businessError) throw businessError;

            setAgents(agentData || []);
            setBusinesses(businessData || []);

        } catch (err) {
            console.error("Supabase Fetch Error:", err);
            // Handling the error if the structural mock returns an issue or if real Supabase fails
            setError(`Failed to load data: ${err.message || "Check your Supabase configuration."}`);
        } finally {
            setLoading(false);
        }
    };

    // Initial load and Polling setup
    useEffect(() => {
        // Initial data fetch
        fetchData();

        // Setup polling (e.g., fetch data every 30 seconds to simulate real-time)
        const pollInterval = setInterval(fetchData, 3000000); 

        // Cleanup function
        return () => clearInterval(pollInterval);
    }, []); // Runs once on mount

    // Data Processing, Calculation, and Sorting Logic
    const agentPerformance = useMemo(() => {
        if (loading || agents.length === 0) return []; 

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // Convert filter dates to milliseconds for comparison
        const startFilter = new Date(startDate).getTime();
        const endFilter = new Date(endDate);
        // Set end date to end of day (23:59:59.999) for inclusive filtering
        endFilter.setHours(23, 59, 59, 999);
        const endFilterTime = endFilter.getTime();
        
        // 1. Map agents to their calculated performance metrics
        const calculatedPerformance = agents.map(agent => {
            const agentReferral = agent.referral ? String(agent.referral).toLowerCase() : null;
            
            // Filter businesses for this agent (case-insensitive check on referral code)
            const agentBusinesses = businesses.filter(business => 
                business.referral && String(business.referral).toLowerCase() === agentReferral
            );

            // Calculate Metrics
            const totalCustomers = agentBusinesses.length;

            const customersLast30Days = agentBusinesses.filter(business => 
                business.referral_activated_at && new Date(business.referral_activated_at).getTime() >= thirtyDaysAgo.getTime()
            ).length;

            const customersInSpan = agentBusinesses.filter(business => {
                if (!business.referral_activated_at) return false;
                const activatedTime = new Date(business.referral_activated_at).getTime();
                return activatedTime >= startFilter && activatedTime <= endFilterTime;
            }).length;

            return {
                ...agent,
                totalCustomers,
                customersLast30Days,
                customersInSpan,
            };
        });

        // 2. Sort the performance data: 
        // Primary sort: highest 'customersInSpan' first (descending)
        // Secondary sort (tie-breaker): highest 'totalCustomers' first (descending)
        calculatedPerformance.sort((a, b) => {
            if (b.customersInSpan !== a.customersInSpan) {
                return b.customersInSpan - a.customersInSpan;
            }
            return b.totalCustomers - a.totalCustomers;
        });

        return calculatedPerformance;
    }, [agents, businesses, startDate, endDate, loading]);


    if (loading) {
        return (
            <div className='w-full flex flex-col items-center bg-[#f9f9f9] min-h-screen pt-20'>
                <AdminBar />
                <div className="text-xl text-gray-600 p-10">Loading agent data from Supabase...</div>
            </div>
        );
    }
    
    // Note: If the Supabase client isn't configured, this will display "No agents found."
    return (
        <div className='w-full flex flex-col items-center bg-[#f9f9f9] pt-[80px] pb-[30px] min-h-screen'>
                   <Helmet>
                    <title>Agents | Sitemanifest</title> 
                    
                    <meta name="robots" content="noindex, nofollow" />
                    
                    <meta name="googlebot" content="noarchive" />
                  </Helmet>
            <AdminBar />

            <div className='w-full max-w-6xl px-4'>
                {error && (
                    <div className="p-4 mb-6 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}
                
                {/* Calendar Filter Selection */}
                <div className='bg-white shadow-lg rounded-xl p-6 mb-8 border border-gray-200'>
                    <h2 className='text-xl font-bold text-gray-800 mb-4'>Filter Performance by Date Span</h2>
                    <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 items-center'>
                        <div className='flex flex-col'>
                            <label htmlFor="startDate" className='text-sm font-medium text-gray-600 mb-1'>Start Date</label>
                            <input 
                                id="startDate"
                                type="date" 
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className='p-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500'
                            />
                        </div>
                        <div className='flex flex-col'>
                            <label htmlFor="endDate" className='text-sm font-medium text-gray-600 mb-1'>End Date</label>
                            <input 
                                id="endDate"
                                type="date" 
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className='p-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500'
                            />
                        </div>
                        <div className='text-sm text-gray-500 pt-6'>
                            <p>Filtering from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>

                {/* Agent Cards Mapping */}
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
                    {agentPerformance.length === 0 && !loading && (
                        <div className="md:col-span-2 xl:col-span-3 text-center p-10 bg-white rounded-xl shadow-lg text-gray-500">
                            No agents found. Please ensure your Supabase connection is active and data exists in the 'agents' and 'businesses' tables.
                        </div>
                    )}

                    {/* Agents are sorted by customersInSpan (highest first) */}
                    {agentPerformance.map((agent) => (
                        <div 
                            key={agent.id} 
                            className='bg-white shadow-xl rounded-xl p-6 flex flex-col gap-4 transition-all duration-300 hover:shadow-2xl border-t-4 border-[#1E7D6B]'
                        >
                            {/* Top Row: Agent Info */}
                            <div className='flex items-center justify-between border-b pb-3'>
                                <h3 className='text-2xl font-bold text-gray-800'>{agent.name || 'Unnamed Agent'}</h3>
                                <span className='bg-gray-100 text-sm font-mono text-gray-600 px-3 py-1 rounded-full' title={agent.id}>
                                    ID: {agent.id}
                                </span>
                            </div>

                            {/* Referral Code */}
                            <div className='flex justify-between items-center text-gray-600'>
                                <span className='font-medium'>Referral Code:</span>
                                <span className='font-bold text-[#1E7D6B]'>{agent.referral || 'N/A'}</span>
                            </div>

                            {/* Middle Row: Aggregate Totals */}
                            <div className='grid grid-cols-2 gap-3 pt-3 border-t'>
                                <div className='p-3 bg-green-50 rounded-lg text-center shadow-inner'>
                                    <p className='text-sm text-gray-600'>Total Customers</p>
                                    <p className='text-2xl font-extrabold text-[#1E7D6B]'>{agent.totalCustomers}</p>
                                </div>
                                <div className='p-3 bg-blue-50 rounded-lg text-center shadow-inner'>
                                    <p className='text-sm text-gray-600'>Customers (Last 30 Days)</p>
                                    <p className='text-2xl font-extrabold text-blue-600'>{agent.customersLast30Days}</p>
                                </div>
                            </div>
                            
                            {/* Last Row: Calendar Span Total */}
                            <div className='mt-4 pt-4 border-t border-dashed'>
                                <div className='p-3 bg-yellow-50 rounded-lg text-center'>
                                    <p className='text-sm text-gray-700 font-semibold mb-1'>Customers in Selected Span</p>
                                    <p className='text-3xl font-extrabold text-yellow-700'>{agent.customersInSpan}</p>
                                    <p className='text-xs text-gray-500 mt-1'>({new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()})</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}